import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Buildings, Wallet, TrendUp, CurrencyCircleDollar, Storefront, Link as LinkIcon, Copy, PencilSimple, CheckCircle } from "@phosphor-icons/react";

export default function ResellerDashboard() {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [reseller, setReseller] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const [m, r] = await Promise.all([api.get("/reseller/metrics"), api.get("/reseller/me")]);
      setMetrics(m.data);
      setReseller(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const brand = user?.reseller_brand_color || reseller?.brand_color || "#0F4C3A";
  const publicUrl = reseller?.slug ? `${window.location.origin}/r/${reseller.slug}` : "";
  const copyLink = () => {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true); toast.success("Link copiado");
    setTimeout(() => setCopied(false), 2000);
  };

  const cards = [
    { label: "Saldo de créditos", value: metrics?.credit_balance ?? "—", icon: Wallet, color: metrics?.credit_balance < 5 ? "#DC2626" : brand, testid: "metric-balance", link: "/reseller/billing" },
    { label: "Ganhos disponíveis", value: metrics ? `R$ ${metrics.earnings_balance.toFixed(2)}` : "—", icon: CurrencyCircleDollar, color: brand, testid: "metric-earnings", link: "/reseller/billing" },
    { label: "Empresas ativas", value: metrics?.companies_active ?? "—", icon: Buildings, color: brand, testid: "metric-active", link: "/reseller/companies" },
    { label: "Empresas bloqueadas", value: metrics?.companies_blocked ?? "—", icon: Buildings, color: "#D97706", testid: "metric-blocked", link: "/reseller/companies" },
    { label: "MRR (BRL)", value: metrics ? `R$ ${metrics.mrr.toFixed(2)}` : "—", icon: TrendUp, color: brand, testid: "metric-mrr" },
    { label: "Comissão", value: metrics ? `${metrics.commission_rate.toFixed(1)}%` : "—", icon: TrendUp, color: brand, testid: "metric-commission" },
  ];

  return (
    <div className="p-8">
      <div className="mb-8">
        <p className="text-xs font-semibold tracking-[0.3em] uppercase" style={{ color: brand }}>Painel</p>
        <h1 className="text-4xl font-black tracking-tighter flex items-center gap-3">
          <Storefront size={36} weight="duotone" style={{ color: brand }} />
          {user?.reseller_name}
        </h1>
        <p className="text-sm text-[#5C5C59] mt-2">Visão geral da sua operação white-label.</p>
      </div>

      <div className="bg-white border border-[#E5E5E0] shadow-brutal p-5 mb-6" data-testid="landing-link-card">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <LinkIcon size={24} weight="duotone" style={{ color: brand }} />
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-widest font-bold text-[#5C5C59]">Sua página pública de venda</p>
              <p className="font-mono text-sm mt-1 truncate" data-testid="landing-url">{publicUrl}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={copyLink} data-testid="copy-landing-btn" size="sm" className="rounded-none text-white" style={{ background: brand }}>
              {copied ? <CheckCircle size={14} weight="bold" className="mr-1" /> : <Copy size={14} weight="bold" className="mr-1" />}
              {copied ? "Copiado!" : "Copiar link"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="edit-landing-btn" className="rounded-none">
              <PencilSimple size={14} weight="bold" className="mr-1" /> Personalizar
            </Button>
          </div>
        </div>
        <p className="text-xs text-[#5C5C59] mt-2">
          Envie este link pros seus clientes. Eles escolhem um plano, pagam via Pix e a conta é criada automaticamente.
          Você ganha <b>{reseller?.commission_rate?.toFixed(1) || 0}% de comissão</b> sobre cada venda.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.testid} className="bg-white border border-[#E5E5E0] shadow-brutal p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C59]">{c.label}</p>
                <p className="text-3xl font-black tracking-tighter mt-2" style={{ color: c.color }}>{c.value}</p>
              </div>
              <c.icon size={22} weight="duotone" style={{ color: c.color }} />
            </div>
            {c.link && (
              <Link to={c.link} className="text-[10px] uppercase tracking-wider font-semibold mt-3 inline-block hover:underline" style={{ color: brand }}>
                Ver detalhes →
              </Link>
            )}
          </div>
        ))}
      </div>

      {metrics?.credit_balance !== undefined && metrics.credit_balance < 5 && (
        <div className="bg-[#FEF3C7] border-l-[3px] border-[#D97706] p-4 mb-6">
          <p className="text-[10px] uppercase tracking-widest font-bold text-[#92400E]">Créditos baixos</p>
          <p className="text-sm mt-1">Você tem apenas <b>{metrics.credit_balance}</b> créditos. Recarregue via Pix pra continuar criando empresas manualmente.</p>
          <Link to="/reseller/billing" className="inline-block mt-2 text-xs font-bold uppercase tracking-wider text-[#92400E] underline">Recarregar agora</Link>
        </div>
      )}

      <EditLandingDialog open={editOpen} onOpenChange={setEditOpen} reseller={reseller} onDone={load} brand={brand} />
    </div>
  );
}

function EditLandingDialog({ open, onOpenChange, reseller, onDone, brand }) {
  const [form, setForm] = useState({ slug: "", brand_color: "#0F4C3A", landing_headline: "", landing_subtitle: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  useEffect(() => {
    if (open && reseller) {
      setForm({
        slug: reseller.slug || "",
        brand_color: reseller.brand_color || "#0F4C3A",
        landing_headline: reseller.landing_headline || "",
        landing_subtitle: reseller.landing_subtitle || "",
      });
      setLogoUrl(reseller.logo_url || null);
    }
  }, [open, reseller?.id]);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) return toast.error("Logo muito grande (máx. 500KB). Comprima antes.");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/reseller/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoUrl(data.logo_url);
      toast.success("Logo enviado");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const removeLogo = async () => {
    if (!window.confirm("Remover o logo?")) return;
    try {
      await api.delete("/reseller/logo");
      setLogoUrl(null);
      toast.success("Logo removido");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.patch("/reseller/me", form);
      toast.success("Página atualizada");
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-none border-[#E5E5E0] max-w-lg max-h-[85vh] overflow-y-auto" data-testid="edit-landing-dialog">
        <DialogHeader><DialogTitle>Personalizar página pública</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase font-semibold tracking-wider">Logo</Label>
            <div className="flex items-center gap-3 mt-1">
              {logoUrl ? (
                <img src={logoUrl} alt="Logo" className="h-16 w-16 object-cover border border-[#E5E5E0]" data-testid="logo-preview" />
              ) : (
                <div className="h-16 w-16 border border-dashed border-[#E5E5E0] flex items-center justify-center text-[10px] text-[#5C5C59]">
                  Sem logo
                </div>
              )}
              <div className="flex-1 flex flex-col gap-1">
                <label className="rounded-none text-white px-3 py-2 text-sm text-center cursor-pointer" style={{ background: brand }}>
                  {uploading ? "Enviando…" : "Escolher imagem"}
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={uploadLogo} disabled={uploading} data-testid="logo-file-input" />
                </label>
                {logoUrl && (
                  <button type="button" onClick={removeLogo} className="text-[10px] uppercase tracking-wider text-[#DC2626] hover:underline" data-testid="logo-remove-btn">
                    Remover
                  </button>
                )}
                <p className="text-[10px] text-[#5C5C59]">PNG/JPG/WEBP, máx. 500KB.</p>
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold tracking-wider">Slug do link ({window.location.origin}/r/…)</Label>
            <Input data-testid="edit-slug-input" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="rounded-none mt-1 font-mono" placeholder="meu-negocio" />
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold tracking-wider">Cor da marca</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input type="color" value={form.brand_color} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className="rounded-none w-14 h-10 p-1" />
              <Input value={form.brand_color} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className="rounded-none flex-1 font-mono" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold tracking-wider">Título principal</Label>
            <Input data-testid="edit-headline-input" value={form.landing_headline} onChange={(e) => setForm({ ...form, landing_headline: e.target.value })} className="rounded-none mt-1" placeholder="Cadastre-se e comece a atender…" />
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold tracking-wider">Subtítulo</Label>
            <Input data-testid="edit-subtitle-input" value={form.landing_subtitle} onChange={(e) => setForm({ ...form, landing_subtitle: e.target.value })} className="rounded-none mt-1" placeholder="Escolha um plano abaixo…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-none">Cancelar</Button>
          <Button data-testid="submit-edit-landing" onClick={submit} disabled={saving} className="rounded-none text-white" style={{ background: brand }}>
            {saving ? "Salvando…" : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
